import { emptyExpenseItem } from "./expense-schema";

import type { ExpenseFormValues } from "./expense-schema";

export type TaxOption = "none" | "ppn11" | "pb1_10" | "manual";
export type DiscountOption = "none" | "disc5" | "disc10" | "manual";

/** pricePerUnit is per purchase uom, so money uses qtyInUom (qty is the base-unit qty). */
function moneyQty(item: { qty?: number; qtyInUom?: number | null }): number {
  return Number(item.qtyInUom ?? item.qty) || 0;
}

export function calculateItemsSubtotal(
  items:
    | { qty?: number; qtyInUom?: number | null; pricePerUnit?: number }[]
    | undefined,
): number {
  return (items ?? []).reduce(
    (acc, curr) => acc + moneyQty(curr) * (Number(curr?.pricePerUnit) || 0),
    0,
  );
}

export function calculateExpenseGrandTotal(
  itemsSubtotal: number,
  discount: number,
  tax: number,
): number {
  return Math.max(0, itemsSubtotal - discount + tax);
}

export function inferDiscountOption(
  subtotal: number,
  discount: number,
): DiscountOption {
  if (!discount || discount === 0) return "none";
  if (subtotal > 0 && Math.abs(discount - Math.round(subtotal * 0.05)) <= 1) {
    return "disc5";
  }
  if (subtotal > 0 && Math.abs(discount - Math.round(subtotal * 0.1)) <= 1) {
    return "disc10";
  }
  return "manual";
}

export function inferTaxOption(taxableAmount: number, tax: number): TaxOption {
  if (!tax || tax === 0) return "none";
  if (
    taxableAmount > 0 &&
    Math.abs(tax - Math.round(taxableAmount * 0.11)) <= 1
  ) {
    return "ppn11";
  }
  if (
    taxableAmount > 0 &&
    Math.abs(tax - Math.round(taxableAmount * 0.1)) <= 1
  ) {
    return "pb1_10";
  }
  return "manual";
}

export function buildExpenseSubmitPayload(
  values: ExpenseFormValues,
  existingReceiptKeys: string[],
  uploadedKeys: string[],
) {
  const items =
    values.items && values.items.length > 0 ? values.items : [emptyExpenseItem];
  const totalQty = items.reduce((s, i) => s + (Number(i.qty) || 1), 0);
  const totalAmount = items.reduce(
    (s, i) => s + (moneyQty(i) || 1) * (Number(i.pricePerUnit) || 0),
    0,
  );
  const pricePerUnit =
    items.length === 1 ? (items[0]?.pricePerUnit ?? totalAmount) : totalAmount;
  const description =
    values.description && values.description.trim().length > 0
      ? values.description
      : items
          .map((i) => i.description)
          .filter(Boolean)
          .join(", ");

  const allKeys = [...existingReceiptKeys, ...uploadedKeys];

  return {
    ...values,
    description,
    qty: totalQty,
    pricePerUnit,
    items: items.map((i) => ({
      ...i,
      itemType: i.itemType ?? undefined,
      itemId: i.itemId ?? undefined,
      uomId: i.uomId ?? undefined,
      qtyInUom: i.qtyInUom ?? undefined,
    })),
    receiptKeys: allKeys,
    receiptKey: allKeys[0] || null,
  };
}
